import React, { useEffect, useState } from 'react';
import {
  Menu,
  Dropdown,
  Typography,
  Divider,
  Tag,
  Avatar,
  Tooltip,
  Space,
  Progress,
} from 'antd';
import {
  FireOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  CaretRightOutlined,
  CaretDownOutlined,
  RobotOutlined,
  MenuOutlined,
  AuditOutlined,
  LockOutlined,
  CustomerServiceOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  GiftOutlined,
  TrophyOutlined,
  SwapOutlined,
  DashboardOutlined,
  SearchOutlined,
  FileSearchOutlined,
  EditOutlined,
  HighlightOutlined,
  PictureOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  FormOutlined,
  DatabaseOutlined,
  FormatPainterOutlined,
  SketchOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { logout, getUserProfile } from '../auth';
import moment from 'moment-timezone';
import ChannelTalkService from '../services/ChannelService';
import CreditDisplay from './Common/CreditDisplay';
import { creditsApi, gamificationApi } from '../api';
import { getRankDisplayName } from './Gamification/RankComponents';

const { Text } = Typography;

const planTags = {
  // フリープラン：薄いグレー
  free: { color: 'default', text: 'フリープラン' },

  // 新旧プラン共通
  // ライトプラン：青系
  lite: { color: 'blue', text: 'ライトプラン' },
  // 旧スタンダードプラン：緑系
  standard: { color: 'success', text: 'スタンダードプラン' },

  // 新スタンダードプラン：緑系
  standard_new: { color: 'success', text: 'スタンダードプラン' },
  // 旧プロプラン：青系
  pro: { color: 'blue', text: 'プロプラン' },

  // プレミアムプラン：ゴールド
  premium: { color: 'warning', text: 'プレミアムプラン' },
  premium_new: { color: 'warning', text: 'プレミアムプラン' },

  // カスタムプラン：パープル系
  custom: { color: 'purple', text: 'カスタムプラン' },
};

const AuthenticatedMenu = ({
  userProfile,
  userRole,
  ticketCount,
  isMobile,
  toggleSidebar,
  location: propLocation,
}) => {
  const navigate = useNavigate();
  const location = propLocation || useLocation();
  const [userPlan, setUserPlan] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null);
  const [displayedBonusCredit, setDisplayedBonusCredit] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [currentRank, setCurrentRank] = useState(null);

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const profile = await getUserProfile();
        if (profile && profile.plan) {
          setUserPlan(profile.plan);
        } else {
          console.error('User profile or plan is missing');
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }
    };
    fetchUserPlan();
  }, []);

  // クレジット情報取得
  useEffect(() => {
    const fetchCreditInfo = async () => {
      try {
        const info = await creditsApi.getMyCredits();
        setCreditInfo(info);
      } catch (error) {
        console.error('Failed to fetch credit info:', error);
      }
    };

    fetchCreditInfo();

    // 30秒ごとに更新
    const interval = setInterval(fetchCreditInfo, 30000);
    return () => clearInterval(interval);
  }, []);

  // ランク情報取得
  useEffect(() => {
    const fetchRankStatus = async () => {
      try {
        const status = await gamificationApi.getStatus();
        setCurrentRank(status?.current_rank || null);
      } catch (error) {
        console.error('Failed to fetch rank status:', error);
      }
    };

    fetchRankStatus();
  }, []);

  // ボーナスクレジットのカウントアップアニメーション
  useEffect(() => {
    if (!creditInfo) return;

    const targetBonus = creditInfo.bonus_credit || 0;
    const currentBonus = displayedBonusCredit;

    if (targetBonus === currentBonus) return;

    const difference = targetBonus - currentBonus;
    const duration = 1000; // 1秒
    const steps = 30;
    const increment = difference / steps;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      if (currentStep >= steps) {
        setDisplayedBonusCredit(targetBonus);
        clearInterval(timer);
      } else {
        setDisplayedBonusCredit(
          Math.round(currentBonus + increment * currentStep),
        );
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [creditInfo?.bonus_credit]);

  const handleLogout = () => {
    // チャネルトークシャットダウン
    ChannelTalkService.shutdown();

    // 匿名ユーザー用に再初期化
    ChannelTalkService.bootAnonymous();

    // 既存のログアウト処理
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const planTag =
    userProfile && userProfile.plan
      ? (() => {
          const plan = userProfile.plan;
          const subscription = userProfile.subscription;
          const planName = plan.is_legacy
            ? `${plan.display_name}（Legacy）`
            : plan.display_name || planTags.free.text;

          let displayText = planName;
          if (subscription && subscription.status === 'trialing') {
            const trialEnd = subscription.trial_info?.trial_end || subscription.current_period_end;
            if (trialEnd) {
              const endDate = moment(trialEnd).tz('Asia/Tokyo').format('MM/DD');
              displayText = `${planName} (トライアル ${endDate}まで)`;
            } else {
              displayText = `${planName} (トライアル中)`;
            }
          } else if (
            subscription &&
            subscription.status === 'canceling' &&
            subscription.current_period_end
          ) {
            const endDate = moment(subscription.current_period_end)
              .tz('Asia/Tokyo')
              .format('MM/DD');
            displayText = `${planName} (${endDate}まで)`;
          }

          // ランク表示名を取得
          const rankDisplayName = currentRank
            ? getRankDisplayName(currentRank)
            : null;

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '4px',
                marginBottom: '4px',
              }}
            >
              <Tag
                color={planTags[plan.name]?.color || planTags.free.color}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                }}
              >
                {displayText}
              </Tag>
              {rankDisplayName && (
                <Tag
                  color="gold"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: 0,
                  }}
                >
                  {rankDisplayName}
                </Tag>
              )}
            </div>
          );
        })()
      : null;

  // メニューアイテムのスタイル
  const menuItemStyle = {
    padding: '10px 14px',
    cursor: 'pointer',
    borderRadius: '6px',
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#333',
  };

  const menuItemHoverStyle = {
    backgroundColor: '#f5f5f5',
  };

  const MenuItemComponent = ({
    icon,
    label,
    onClick,
    danger = false,
    isActive = false,
  }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    return (
      <div
        style={{
          ...menuItemStyle,
          ...(isHovered ? menuItemHoverStyle : {}),
          ...(danger ? { color: '#ff4d4f' } : {}),
          ...(isActive ? { color: '#1890ff', fontWeight: 500 } : {}),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
      >
        {React.cloneElement(icon, {
          style: {
            ...icon.props.style,
            ...(isActive ? { color: '#1890ff' } : {}),
          },
        })}
        <span>{label}</span>
      </div>
    );
  };

  const menu = (
    <div
      style={{
        width: '420px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        padding: '12px',
      }}
    >
      {/* ヘッダー: ユーザー情報 */}
      <div
        style={{
          padding: '8px 12px 12px',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: '12px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Text style={{ fontSize: '13px', color: '#666' }}>
            {userProfile.email}
          </Text>
          {planTag}
        </div>
      </div>

      {/* クレジット表示 */}
      <div
        style={{
          padding: '8px 12px 12px',
          borderBottom: '1px solid #f0f0f0',
          marginBottom: '12px',
        }}
      >
        <CreditDisplay creditInfo={creditInfo} showBonus={true} size="small" />
      </div>

      {/* 2カラムメニュー */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '4px',
        }}
      >
        {/* 左カラム: アカウント管理 */}
        <div>
          <div
            style={{
              fontSize: '11px',
              color: '#999',
              padding: '4px 14px 8px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            アカウント
          </div>
          <MenuItemComponent
            icon={
              <UserOutlined style={{ fontSize: '14px', color: '#595959' }} />
            }
            label="マイページ"
            onClick={() => navigate('/mypage')}
            isActive={location.pathname === '/mypage'}
          />
          <MenuItemComponent
            icon={
              <TrophyOutlined style={{ fontSize: '14px', color: '#595959' }} />
            }
            label="ランクシステム"
            onClick={() => navigate('/rank-system')}
            isActive={location.pathname === '/rank-system'}
          />
          <MenuItemComponent
            icon={
              <GiftOutlined style={{ fontSize: '14px', color: '#595959' }} />
            }
            label="友達紹介"
            onClick={() => navigate('/referral')}
            isActive={location.pathname === '/referral'}
          />
          <MenuItemComponent
            icon={
              <SettingOutlined style={{ fontSize: '14px', color: '#595959' }} />
            }
            label="登録情報変更"
            onClick={() => navigate('/settings')}
            isActive={location.pathname === '/settings'}
          />
        </div>

        {/* 右カラム: プラン・クレジット */}
        <div>
          <div
            style={{
              fontSize: '11px',
              color: '#999',
              padding: '4px 14px 8px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            プラン・クレジット
          </div>
          <MenuItemComponent
            icon={
              <CreditCardOutlined
                style={{ fontSize: '14px', color: '#595959' }}
              />
            }
            label="サブスク管理"
            onClick={() => navigate('/subscription-management')}
            isActive={location.pathname === '/subscription-management'}
          />
          <MenuItemComponent
            icon={
              <SwapOutlined style={{ fontSize: '14px', color: '#595959' }} />
            }
            label="プラン変更"
            onClick={() => navigate('/plan')}
            isActive={location.pathname === '/plan'}
          />
          <MenuItemComponent
            icon={
              <FireOutlined style={{ fontSize: '14px', color: '#595959' }} />
            }
            label="ボーナスクレジット"
            onClick={() => navigate('/bonus-credit')}
            isActive={location.pathname === '/bonus-credit'}
          />
        </div>
      </div>

      {/* ログアウト */}
      <div
        style={{
          borderTop: '1px solid #f0f0f0',
          marginTop: '12px',
          paddingTop: '8px',
        }}
      >
        <MenuItemComponent
          icon={<LogoutOutlined style={{ fontSize: '14px' }} />}
          label="ログアウト"
          onClick={handleLogout}
          danger
        />
      </div>
    </div>
  );

  // モード切替メニュー用のアイテムスタイル
  const modeMenuItemStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    cursor: 'pointer',
    borderRadius: 6,
    transition: 'background 0.2s',
    fontSize: 13,
    color: '#333',
  };

  const modeMenuIconStyle = {
    marginRight: 8,
    fontSize: 14,
    color: '#595959',
  };

  const modeMenuGroupTitleStyle = {
    fontSize: 10,
    color: '#bbb',
    fontWeight: 500,
    marginBottom: 6,
    paddingLeft: 4,
    letterSpacing: '0.5px',
  };

  // モードヘッダー（アイコン上部、●背景白アイコン、下線付き）
  const ModeHeader = ({ icon: Icon, color, title, onClick, isActive }) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: 16,
        cursor: onClick ? 'pointer' : 'default',
        paddingBottom: 12,
        borderBottom: `2px solid ${color}20`,
        transition: 'opacity 0.2s, transform 0.2s',
        opacity: 1,
      }}
      onClick={onClick}
      onMouseEnter={e => {
        if (onClick) {
          e.currentTarget.style.opacity = '0.75';
          e.currentTarget.style.transform = 'scale(0.98)';
        }
      }}
      onMouseLeave={e => {
        if (onClick) {
          e.currentTarget.style.opacity = '1';
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 8,
          boxShadow: isActive ? `0 0 0 3px ${color}40` : 'none',
        }}
      >
        <Icon style={{ fontSize: 18, color: '#fff' }} />
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: color,
          textAlign: 'center',
        }}
      >
        {title}
      </span>
    </div>
  );

  // ページタイプの判定（modeMenuで使用）
  const isSEOArticlePage = location.pathname.startsWith('/seo-article');
  const isFactCheckPage = location.pathname.startsWith('/fact-check');
  const isAIOPage = location.pathname.startsWith('/aio');
  const isSettingsPage = [
    '/settings',
    '/plan',
    '/bonus-credit',
    '/subscription-management',
    '/subscription-success',
    '/subscription-canceled',
    '/mypage',
    '/rank-system',
    '/referral',
  ].some(
    path =>
      location.pathname === path || location.pathname.startsWith(path + '#'),
  );
  const isAdminPage = location.pathname.startsWith('/admin');

  // 3〜4カラムのリッチなモード切替メニュー
  const isAdmin = userRole === 'admin' || userRole === 'master';
  const menuWidth = isAdmin ? 920 : 720;

  const modeMenu = (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
        padding: '20px 24px',
        width: menuWidth,
      }}
    >
      <div style={{ display: 'flex', gap: 28 }}>
        {/* カラム1: AIOコンテンツモード */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ModeHeader
            icon={RobotOutlined}
            color="#0072ff"
            title="AIOコンテンツモード"
            onClick={() => navigate('/aio/dashboard')}
            isActive={isAIOPage}
          />

          <div style={modeMenuGroupTitleStyle}>SEO/AIOリサーチ</div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/aio/analysis/new'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/aio/analysis/new')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <SearchOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/aio/analysis/new'
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            新規リサーチ
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname.startsWith('/aio/analysis/list') ||
              location.pathname.startsWith('/aio/analysis/detail')
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/aio/analysis/list')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <FileSearchOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname.startsWith('/aio/analysis/list') ||
                location.pathname.startsWith('/aio/analysis/detail')
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            リサーチ結果
          </div>

          <div style={{ ...modeMenuGroupTitleStyle, marginTop: 14 }}>
            コンテンツ生成
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/aio/generation/new'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/aio/generation/new')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <FormOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/aio/generation/new'
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            新規生成
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname.startsWith('/aio/generation/list') ||
              location.pathname.startsWith('/aio/generation/detail')
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/aio/generation/list')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <DatabaseOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname.startsWith('/aio/generation/list') ||
                location.pathname.startsWith('/aio/generation/detail')
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            生成結果一覧
          </div>

          <div style={{ ...modeMenuGroupTitleStyle, marginTop: 14 }}>
            カスタマイズ
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/aio/style'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/aio/style')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <FormatPainterOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/aio/style'
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            スタイル管理
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/aio/original'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/aio/original')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <SketchOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/aio/original'
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            独自情報管理
          </div>
        </div>

        {/* 区切り線 */}
        <div style={{ width: 1, background: '#f0f0f0' }} />

        {/* カラム2: その他（ファクトチェック・SEO記事生成） */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ModeHeader
            icon={AppstoreOutlined}
            color="#52c41a"
            title="その他"
            isActive={isFactCheckPage || isSEOArticlePage}
          />

          <div style={modeMenuGroupTitleStyle}>ファクトチェック</div>
          {userProfile?.early_access?.fact_check ? (
            <>
              <div
                style={{
                  ...modeMenuItemStyle,
                  ...(location.pathname === '/fact-check'
                    ? { color: '#1890ff', fontWeight: 500 }
                    : {}),
                }}
                onClick={() => navigate('/fact-check')}
                onMouseEnter={e =>
                  (e.currentTarget.style.background = '#f5f5f5')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <EditOutlined
                  style={{
                    ...modeMenuIconStyle,
                    ...(location.pathname === '/fact-check'
                      ? { color: '#1890ff' }
                      : {}),
                  }}
                />
                新規タスク予約
              </div>
              <div
                style={{
                  ...modeMenuItemStyle,
                  ...(location.pathname.startsWith('/fact-check/tasks') ||
                  location.pathname.startsWith('/fact-check/result')
                    ? { color: '#1890ff', fontWeight: 500 }
                    : {}),
                }}
                onClick={() => navigate('/fact-check/tasks')}
                onMouseEnter={e =>
                  (e.currentTarget.style.background = '#f5f5f5')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <UnorderedListOutlined
                  style={{
                    ...modeMenuIconStyle,
                    ...(location.pathname.startsWith('/fact-check/tasks') ||
                    location.pathname.startsWith('/fact-check/result')
                      ? { color: '#1890ff' }
                      : {}),
                  }}
                />
                検証結果一覧
              </div>
            </>
          ) : (
            <Tooltip title="一部のユーザー様にのみ試験提供中です">
              <div
                style={{
                  ...modeMenuItemStyle,
                  color: '#bbb',
                  cursor: 'not-allowed',
                }}
              >
                <LockOutlined style={{ ...modeMenuIconStyle, color: '#bbb' }} />
                限定機能
              </div>
            </Tooltip>
          )}

          <div style={{ ...modeMenuGroupTitleStyle, marginTop: 18 }}>
            SEO記事生成
            <span style={{ fontSize: 9, color: '#ccc', marginLeft: 4 }}>
              [Legacy]
            </span>
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/seo-article'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/seo-article')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <EditOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/seo-article'
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            新規生成
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname.startsWith('/seo-article/list') ||
              location.pathname.startsWith('/seo-article/detail')
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/seo-article/list')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <DatabaseOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname.startsWith('/seo-article/list') ||
                location.pathname.startsWith('/seo-article/detail')
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            生成結果一覧
          </div>
        </div>

        {/* 区切り線 */}
        <div style={{ width: 1, background: '#f0f0f0' }} />

        {/* カラム3: マイページ */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <ModeHeader
            icon={UserOutlined}
            color="#722ed1"
            title="マイページ"
            onClick={() => navigate('/mypage')}
            isActive={isSettingsPage}
          />

          <div style={modeMenuGroupTitleStyle}>サブスクリプション</div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/subscription-management'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/subscription-management')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <CreditCardOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/subscription-management'
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            サブスクリプション
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/plan'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/plan')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <SwapOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/plan' ? { color: '#1890ff' } : {}),
              }}
            />
            プラン変更
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/bonus-credit'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/bonus-credit')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <GiftOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/bonus-credit'
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            ボーナスクレジット
          </div>

          <div style={{ ...modeMenuGroupTitleStyle, marginTop: 14 }}>
            ランク・紹介
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/rank-system'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/rank-system')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <TrophyOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/rank-system'
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            ランクシステム
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/referral'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/referral')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <GiftOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/referral'
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            友達紹介
          </div>

          <div style={{ ...modeMenuGroupTitleStyle, marginTop: 14 }}>
            アカウント
          </div>
          <div
            style={{
              ...modeMenuItemStyle,
              ...(location.pathname === '/settings'
                ? { color: '#1890ff', fontWeight: 500 }
                : {}),
            }}
            onClick={() => navigate('/settings')}
            onMouseEnter={e => (e.currentTarget.style.background = '#f5f5f5')}
            onMouseLeave={e =>
              (e.currentTarget.style.background = 'transparent')
            }
          >
            <SettingOutlined
              style={{
                ...modeMenuIconStyle,
                ...(location.pathname === '/settings'
                  ? { color: '#1890ff' }
                  : {}),
              }}
            />
            登録情報変更
          </div>
        </div>

        {/* 管理者のみ: カラム4 */}
        {isAdmin && (
          <>
            <div style={{ width: 1, background: '#f0f0f0' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <ModeHeader
                icon={AppstoreOutlined}
                color="#fa541c"
                title="管理者用メニュー"
                onClick={() => navigate('/admin/dashboard')}
                isActive={isAdminPage}
              />

              <div style={modeMenuGroupTitleStyle}>カスタマーサクセス</div>
              <div
                style={{
                  ...modeMenuItemStyle,
                  ...(location.pathname === '/admin/user-management'
                    ? { color: '#1890ff', fontWeight: 500 }
                    : {}),
                }}
                onClick={() => navigate('/admin/user-management')}
                onMouseEnter={e =>
                  (e.currentTarget.style.background = '#f5f5f5')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <TeamOutlined
                  style={{
                    ...modeMenuIconStyle,
                    ...(location.pathname === '/admin/user-management'
                      ? { color: '#1890ff' }
                      : {}),
                  }}
                />
                ユーザー管理
              </div>
              <div
                style={{
                  ...modeMenuItemStyle,
                  ...(location.pathname === '/admin/email-marketing'
                    ? { color: '#1890ff', fontWeight: 500 }
                    : {}),
                }}
                onClick={() => navigate('/admin/email-marketing')}
                onMouseEnter={e =>
                  (e.currentTarget.style.background = '#f5f5f5')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <MailOutlined
                  style={{
                    ...modeMenuIconStyle,
                    ...(location.pathname === '/admin/email-marketing'
                      ? { color: '#1890ff' }
                      : {}),
                  }}
                />
                メール配信
              </div>

              <div style={{ ...modeMenuGroupTitleStyle, marginTop: 14 }}>
                その他
              </div>
              <div
                style={{
                  ...modeMenuItemStyle,
                  ...(location.pathname === '/admin/transactions'
                    ? { color: '#1890ff', fontWeight: 500 }
                    : {}),
                }}
                onClick={() => navigate('/admin/transactions')}
                onMouseEnter={e =>
                  (e.currentTarget.style.background = '#f5f5f5')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <CreditCardOutlined
                  style={{
                    ...modeMenuIconStyle,
                    ...(location.pathname === '/admin/transactions'
                      ? { color: '#1890ff' }
                      : {}),
                  }}
                />
                トランザクション
              </div>
              {userRole === 'master' && (
                <div
                  style={{
                    ...modeMenuItemStyle,
                    ...(location.pathname === '/prompt-management'
                      ? { color: '#1890ff', fontWeight: 500 }
                      : {}),
                  }}
                  onClick={() => navigate('/prompt-management')}
                  onMouseEnter={e =>
                    (e.currentTarget.style.background = '#f5f5f5')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.background = 'transparent')
                  }
                >
                  <SettingOutlined
                    style={{
                      ...modeMenuIconStyle,
                      ...(location.pathname === '/prompt-management'
                        ? { color: '#1890ff' }
                        : {}),
                    }}
                  />
                  プロンプト管理
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // 現在のモード名を取得
  const getCurrentModeName = () => {
    if (isAIOPage) return 'AIOコンテンツモード';
    if (isFactCheckPage) return 'ファクトチェックモード';
    if (isSEOArticlePage) return 'SEO記事生成モード';
    if (isSettingsPage) return 'マイページ';
    if (isAdminPage) return '管理者用メニュー';
    return 'モード選択';
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        position: 'relative',
      }}
    >
      {isMobile && (
        <div className="logo mobile-logo">
          <span>Ignite creativity with..</span>magicss
        </div>
      )}

      {!isMobile && (
        <>
          {/* 左寄せ：モード選択 */}
          <div
            style={{
              flex: 0,
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <Text
              style={{ marginRight: '8px', color: '#666', fontSize: '14px' }}
            >
              モード切替：
            </Text>
            <Dropdown overlay={modeMenu} placement="bottomLeft">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap',
                }}
              >
                <Space size={6}>
                  {isAIOPage && (
                    <RobotOutlined
                      style={{ color: '#0072ff', fontSize: '16px' }}
                    />
                  )}
                  {isFactCheckPage && (
                    <AuditOutlined
                      style={{ color: '#0072ff', fontSize: '16px' }}
                    />
                  )}
                  {isSEOArticlePage && (
                    <FileTextOutlined
                      style={{ color: '#0072ff', fontSize: '16px' }}
                    />
                  )}
                  {isSettingsPage && (
                    <UserOutlined
                      style={{ color: '#0072ff', fontSize: '16px' }}
                    />
                  )}
                  {isAdminPage && (
                    <LockOutlined
                      style={{ color: '#0072ff', fontSize: '16px' }}
                    />
                  )}
                  {isSettingsPage ? (
                    <Text
                      strong
                      style={{
                        color: '#0072ff',
                        fontSize: '14px',
                        cursor: 'pointer',
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        navigate('/mypage');
                      }}
                    >
                      {getCurrentModeName()}
                    </Text>
                  ) : (
                    <Text strong style={{ color: '#0072ff', fontSize: '14px' }}>
                      {getCurrentModeName()}
                    </Text>
                  )}
                  <CaretDownOutlined
                    style={{ fontSize: '12px', color: '#0072ff' }}
                  />
                </Space>
              </div>
            </Dropdown>
          </div>

          {/* 右寄せ：導入サポート、チケット、アイコン */}
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '24px',
            }}
          >
            <a
              href="/support"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#0072ff',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                padding: '5px 12px',
                borderRadius: '4px',
                border: '1px solid #0072ff',
                transition: 'all 0.3s',
                whiteSpace: 'nowrap',
                display: 'inline-flex',
                alignItems: 'center',
                height: '32px',
              }}
              onMouseEnter={e => {
                e.target.style.backgroundColor = '#0072ff';
                e.target.style.color = '#fff';
              }}
              onMouseLeave={e => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#0072ff';
              }}
              onClick={() => {
                ChannelTalkService.trackEvent('support_button_clicked', {
                  button_location: 'header',
                  user_plan: userProfile?.plan?.name || 'free',
                });
              }}
            >
              <CustomerServiceOutlined
                style={{ marginRight: '4px', fontSize: '16px' }}
              />
              法人向けサポートはこちら
            </a>

            {/* クレジット表示（ヘッダー右側、コンパクト版） */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginRight: '16px',
              }}
            >
              {creditInfo ? (
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Tooltip
                    title={
                      <div style={{ minWidth: 220 }}>
                        <table
                          style={{ width: '100%', borderCollapse: 'collapse' }}
                        >
                          <tbody>
                            <tr>
                              <td
                                style={{
                                  padding: '4px 8px 4px 0',
                                  color: 'rgba(255,255,255,0.7)',
                                }}
                              >
                                使用率
                              </td>
                              <td
                                style={{ padding: '4px 0', textAlign: 'right' }}
                              >
                                {Math.round((creditInfo.usage_rate || 0) * 100)}
                                %
                              </td>
                            </tr>
                            <tr>
                              <td
                                style={{
                                  padding: '4px 8px 4px 0',
                                  color: 'rgba(255,255,255,0.7)',
                                }}
                              >
                                残りクレジット
                              </td>
                              <td
                                style={{ padding: '4px 0', textAlign: 'right' }}
                              >
                                {creditInfo.plan_limit === 999999999
                                  ? '∞'
                                  : creditInfo.available_regular.toLocaleString()}
                              </td>
                            </tr>
                            {creditInfo.bonus_credit > 0 && (
                              <tr>
                                <td
                                  style={{
                                    padding: '4px 8px 4px 0',
                                    color: 'rgba(255,255,255,0.7)',
                                  }}
                                >
                                  ボーナス
                                  <span
                                    style={{
                                      fontSize: 10,
                                      marginLeft: 4,
                                      opacity: 0.8,
                                    }}
                                  >
                                    (無期限)
                                  </span>
                                </td>
                                <td
                                  style={{
                                    padding: '4px 0',
                                    textAlign: 'right',
                                    color: '#d3adf7',
                                  }}
                                >
                                  +{creditInfo.bonus_credit.toLocaleString()}
                                </td>
                              </tr>
                            )}
                            <tr>
                              <td
                                colSpan={2}
                                style={{
                                  padding: '8px 0 4px 0',
                                  borderTop: '1px solid rgba(255,255,255,0.2)',
                                }}
                              >
                                <div
                                  style={{
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: 11,
                                  }}
                                >
                                  次回クレジットリセット
                                </div>
                                <div style={{ marginTop: 2 }}>
                                  {creditInfo.next_reset_date ? (
                                    <>
                                      {moment(creditInfo.next_reset_date)
                                        .tz('Asia/Tokyo')
                                        .format('YYYY/MM/DD')}
                                      {creditInfo.days_until_reset > 0 && (
                                        <span
                                          style={{
                                            color: 'rgba(255,255,255,0.7)',
                                            marginLeft: 4,
                                          }}
                                        >
                                          (残り{creditInfo.days_until_reset}日)
                                        </span>
                                      )}
                                    </>
                                  ) : (
                                    <span
                                      style={{ color: 'rgba(255,255,255,0.7)' }}
                                    >
                                      -
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    }
                    placement="bottom"
                  >
                    <Progress
                      type="circle"
                      percent={Math.round((creditInfo.usage_rate || 0) * 100)}
                      width={32}
                      strokeWidth={8}
                      strokeColor={{
                        '0%': '#52c41a',
                        '70%': '#faad14',
                        '90%': '#ff4d4f',
                      }}
                      format={() => null}
                    />
                  </Tooltip>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <FireOutlined
                        style={{ color: '#0072ff', fontSize: '16px' }}
                      />
                      <Text
                        style={{
                          fontSize: '14px',
                          lineHeight: '1.2',
                          fontWeight: 'bold',
                          background:
                            'linear-gradient(90deg, #0072ff 0%, #00d4ff 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        {creditInfo.credit_used} /{' '}
                        {creditInfo.plan_limit === 999999999
                          ? '∞'
                          : creditInfo.plan_limit}
                      </Text>
                    </div>
                    {creditInfo.bonus_credit > 0 && (
                      <Text
                        style={{
                          fontSize: '13px',
                          lineHeight: '1.2',
                          fontWeight: 'bold',
                          background:
                            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                      >
                        +{displayedBonusCredit.toLocaleString()}
                      </Text>
                    )}
                  </div>
                </div>
              ) : (
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  読み込み中...
                </Text>
              )}
            </div>

            <div className="header-icons">
              <Dropdown
                overlay={menu}
                placement="bottomRight"
                onVisibleChange={setUserMenuOpen}
              >
                <div className={`header-icon ${userMenuOpen ? 'active' : ''}`}>
                  <UserOutlined />
                </div>
              </Dropdown>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AuthenticatedMenu;
